<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="goToCreate">
        <md-icon>add</md-icon>
        New Payment
      </md-button>
      <md-button class="md-info md-round" @click="refreshList">
        <md-icon>refresh</md-icon>
        Refresh
      </md-button>
    </template>

    <template #content>
      <!-- Filters -->
      <div class="filters-bar">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>From Date</label>
              <md-input type="date" v-model="filters.from_date" @change="onFilterChange" />
            </md-field>
          </div>

          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>To Date</label>
              <md-input type="date" v-model="filters.to_date" @change="onFilterChange" />
            </md-field>
          </div>

          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>Status</label>
              <md-select v-model="filters.current_status" @md-selected="onFilterChange">
                <md-option value="">All Statuses</md-option>
                <md-option value="draft">Draft</md-option>
                <md-option value="approved">Approved</md-option>
                <md-option value="paid">Paid</md-option>
                <md-option value="cancelled">Cancelled</md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-40 md-small-size-100">
            <md-field>
              <label>Search</label>
              <md-input
                  v-model="filters.search"
                  placeholder="Search by payment number or labourer..."
                  @input="onSearchChange"
              />
            </md-field>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <table-data
          ref="table"
          title="Payments"
          :columns="columns"
          :data="paymentsList"
          :total="total"
          :loading="isLoading"
          :searchable="true"
          search-placeholder="Search by payment number or labourer..."
          storage-key="labourer-payments-list"
          empty-message="No payments found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-primary md-round" @click="goToCreate">
            <md-icon>add</md-icon>
            New Payment
          </md-button>
        </template>

        <template #cell-payment_number="{ item }">
          <clickable-link
              :id="item.id"
              resource="Labourer Payment"
              :text="item.payment_number"
              :stop-propagation="true"
          />
        </template>

        <template #cell-labourer="{ item }">
          <div class="labourer-cell">
            <clickable-link
                :id="item.labourer?item.labourer.id:'xxx'"
                resource="Labourer"
                :text="getLabourerName(item)"
                :stop-propagation="true"
            />
            <strong>{{  }}</strong>
            <small>{{ getLabourerCode(item) }}</small>
          </div>
        </template>

        <template #cell-payment_date="{ item }">
          {{ formatDate(item.payment_date) }}
        </template>

        <template #cell-period="{ item }">
          <span class="period-text">
            {{ formatDate(item.start_date) }} - {{ formatDate(item.end_date) }}
          </span>
        </template>

        <template #cell-total_amount="{ item }">
          <strong class="amount">{{ formatCurrency(item.total_amount) }}</strong>
        </template>

        <template #cell-current_status="{ item }">
          <badge :type="getStatusBadge(item.current_status)">
            {{ item.current_status || 'Draft' }}
          </badge>
        </template>

        <template #actions="{ item }">
          <md-button
              class="md-icon-button md-primary"
              @click="viewPayment(item)"
              title="View"
          >
            <md-icon>visibility</md-icon>
          </md-button>

          <md-button
              v-if="item.current_status === 'draft'"
              class="md-icon-button md-info"
              @click="editPayment(item)"
              title="Edit"
          >
            <md-icon>edit</md-icon>
          </md-button>

          <md-button
              v-if="item.current_status === 'draft'"
              class="md-icon-button md-success"
              @click="approvePayment(item)"
              title="Approve"
          >
            <md-icon>check_circle</md-icon>
          </md-button>

          <md-button
              v-if="item.current_status === 'approved'"
              class="md-icon-button md-info"
              @click="markAsPaid(item)"
              title="Mark as Paid"
          >
            <md-icon>payment</md-icon>
          </md-button>
        </template>

        <template #empty-actions>
          <md-button class="md-primary md-round" @click="goToCreate">
            <md-icon>add</md-icon>
            Create First Payment
          </md-button>
        </template>
      </table-data>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import ClickableLink from '@/components/ClickableLink.vue';
import Badge from '@/components/Badge.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';
import _ from 'lodash';
import TableData from "@/components/DataTable/TableData.vue";

export default {
  name: 'ListLabourerPaymentPage',
  components: {
    TableData,
    PageLayout: PageLayout,
    ClickableLink: ClickableLink,
    Badge: Badge
  },
  mixins: [PageMixin],
  data: function () {
    return {
      paymentsList: [],
      total: 0,
      isLoading: false,
      rowLoading: {},
      filters: {
        from_date: null,
        to_date: null,
        current_status: ''
      },
      columns: [
        { field: 'payment_number', label: 'Payment #' },
        { field: 'labourer', label: 'Labourer' },
        { field: 'payment_date', label: 'Payment Date' },
        { field: 'period', label: 'Period' },
        { field: 'total_items', label: 'Items' },
        { field: 'total_amount', label: 'Amount', align: 'text-right' },
        { field: 'current_status', label: 'Status' }
      ],
      tableState: {
        page: 1,
        perPage: 15,
        search: ''
      }
    };
  },

  computed: {
    pageTitle: function () { return 'Labourer Payments'; },
    pageBreadcrumb: function () {
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Labourer Payments' }
      ];
    },
    pageInfoCards: function () {
      var draftCount = 0;
      var approvedCount = 0;
      var paidCount = 0;
      var totalAmount = 0;

      this.paymentsList.forEach(function (item) {
        if (item.current_status === 'draft') draftCount++;
        else if (item.current_status === 'approved') approvedCount++;
        else if (item.current_status === 'paid') paidCount++;
        totalAmount += parseFloat(item.total_amount || 0);
      });

      return [
        { icon: 'edit_note', label: 'Draft', value: draftCount, color: 'warning' },
        { icon: 'check_circle', label: 'Approved', value: approvedCount, color: 'info' },
        { icon: 'paid', label: 'Paid', value: paidCount, color: 'success' },
        { icon: 'attach_money', label: 'Total Amount', value: this.formatCurrency(totalAmount), color: 'primary' }
      ];
    }
  },
  created: function () {
    this.fetchList();
  },
  methods: {

    async onTableFetch(state) {
      this.tableState = state;
      await this.fetchList();
    },

    async fetchList() {
      this.isLoading = true;
      try {
        var params = {
          include: 'labourer',
          page: {
            number: this.tableState.page,
            size: this.tableState.perPage
          },
          filter: {}
        };

        if (this.filters.from_date) params.filter.from_date = this.filters.from_date;
        if (this.filters.to_date) params.filter.to_date = this.filters.to_date;
        if (this.filters.current_status) params.filter.current_status = this.filters.current_status;
        if (this.tableState.search) params.filter.search = this.tableState.search;

        await this.$store.dispatch('labourerPayments/list', params);
        this.paymentsList = this.$store.getters['labourerPayments/list'] || [];
        this.total = this.$store.getters['labourerPayments/listTotal'] || 0;
      } catch (error) {
        console.error('Error fetching payments:', error);
        this.$store.dispatch('alerts/error', 'Failed to load payments');
        this.paymentsList = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },


    onFilterChange() {
      if (this.$refs.table) {
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
      }
      this.$refs.table && this.$refs.table.emitFetch();
    },

    refreshList() {
      this.fetchList();
    },

    isRowLoading(id) {
      return !!this.rowLoading[id];
    },

    setRowLoading(id, value) {
      this.$set(this.rowLoading, id, value);
    },

    goToCreate() {
      this.$router.push({ name: 'Ready Labourer Payments' });
    },

    viewPayment(item) {
      this.$router.push({ name: 'View Labourer Payment', params: { id: item.id } });
    },

    editPayment(item) {
      this.$router.push({ name: 'Edit Labourer Payment', params: { id: item.id } });
    },

    async approvePayment(item) {
      var self = this;
      var result = await Swal.fire({
        title: 'Approve Payment?',
        text: 'Are you sure you want to approve ' + item.payment_number + '?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4caf50',
        confirmButtonText: 'Yes, approve!'
      });

      if (!result.value) return;

      this.setRowLoading(item.id, true);
      try {
        await this.$store.dispatch('labourerPayments/approvePaymentAction', item.id);
        await this.fetchList();
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to approve payment');
      } finally {
        this.setRowLoading(item.id, false);
      }
    },

    async markAsPaid(item) {
      var result = await Swal.fire({
        title: 'Mark as Paid?',
        text: 'Confirm that ' + item.payment_number + ' has been paid.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#2196f3',
        confirmButtonText: 'Yes, mark as paid!'
      });

      if (!result.value) return;

      this.setRowLoading(item.id, true);
      try {
        await this.$store.dispatch('labourerPayments/markAsPaidAction', item.id);
        await this.fetchList();
      } catch (error) {
        this.$store.dispatch('alerts/error', 'Failed to mark payment as paid');
      } finally {
        this.setRowLoading(item.id, false);
      }
    },

    getLabourerName(item) {
      return item.labourer
          ? (item.labourer.full_name || item.labourer.name || 'Unknown')
          : 'N/A';
    },

    getLabourerCode(item) {
      return item.labourer
          ? (item.labourer.emp_code || item.labourer.labourer_number || '')
          : '';
    },

    getStatusBadge(status) {
      var badges = {
        draft: 'warning',
        approved: 'info',
        paid: 'success',
        cancelled: 'danger'
      };
      return badges[status] || 'info';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    },

    formatCurrency(value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    onSearchChange: _.debounce(function () {
      this.pagination.currentPage = 1;
      this.fetchList();
    }, 500),
  }
};
</script>

<style lang="scss" scoped>
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

.labourer-cell {
  display: flex;
  flex-direction: column;

  strong {
    font-size: 14px;
    color: $text-primary;
  }

  small {
    font-size: 12px;
    margin-top: 2px;
  }
}

.amount {
  color: $brand-success;
}
</style>